import { isNative } from './platform';
import { router } from '@/router';

/**
 * Deep links (Universal Links iOS / App Links Android).
 *
 * Cuando la app se abre por un enlace `https://stories.knooowy.com/...` (p. ej.
 * el magic link de invitación o el de reset de contraseña), enrutamos la app a
 * esa ruta.
 *
 * IMPORTANTE: navegar con la API del router (`router.navigate`), NO con
 * `window.location.hash`. El data router (`createHashRouter`) no se sincroniza
 * bien con cambios manuales del hash: la primera navegación "cuela", pero deja
 * su estado interno inconsistente y cualquier navegación posterior de la app
 * (`useNavigate` / `<Navigate>` / ProtectedRoute) deja de surtir efecto
 * (p. ej. /app se quedaba con el spinner sin poder ir a login/dashboard).
 *
 * No-op en web (los enlaces abren la web normalmente).
 */
export async function initDeepLinks(): Promise<void> {
  if (!isNative()) return;

  try {
    const { App } = await import('@capacitor/app');

    const handleUrl = (url: string | undefined): void => {
      if (!url) return;
      try {
        const parsed = new URL(url);
        // Solo enlaces de nuestro dominio.
        if (!parsed.hostname.endsWith('knooowy.com')) return;
        const target = `${parsed.pathname}${parsed.search}`;
        if (target && target !== '/') {
          void router.navigate(target);
        }
      } catch {
        // URL no parseable: ignorar.
      }
    };

    // Enlace tocado con la app ya abierta.
    await App.addListener('appUrlOpen', (event) => handleUrl(event.url));

    // Arranque en frío desde un enlace.
    const launch = await App.getLaunchUrl();
    handleUrl(launch?.url);
  } catch (err) {
    console.error('initDeepLinks failed:', err);
  }
}
